import React, { useCallback } from 'react';

import { getDepartments } from '@vlabs/api/artemy/client';
import type { DepartmentId } from '@vlabs/api/artemy/model';
import validate from '@vlabs/shared/validators';
import { Button } from '@vlabs/uikit/components/controls/button/Button';
import { Input } from '@vlabs/uikit/components/controls/input/Input';
import type { TOption } from '@vlabs/uikit/types';
import { useForm } from 'react-hook-form';

import { OwnerDepartmentSelect } from '@vlabs/monokit/shared/ui/owner-department-select';
import { ControllableValueSelect } from '@vlabs/monokit/shared/ui/value-select';

import { useCreateListFormTranslation } from './i18n';
import st from './ListFormPoint3.module.sass';

interface ListFormValues {
  user_data: string;
  departmentId?: DepartmentId;
}

interface ListFormPoint3Props {
  context?: 'create' | 'duplicate';
  defaultValues?: ListFormValues;
  duplicateSourceName?: string;
  initialDepartmentId?: DepartmentId;
  onCancel?: VoidFunction;
  onSubmit: (values: ListFormValues) => void;
}

function ListFormPoint3({
  context = 'create',
  defaultValues = undefined,
  duplicateSourceName = undefined,
  initialDepartmentId = '',
  onCancel = undefined,
  onSubmit,
}: ListFormPoint3Props): React.JSX.Element {
  const { t } = useCreateListFormTranslation();

  const form = useForm<ListFormValues>({
    defaultValues: {
      user_data: defaultValues?.user_data ?? '',
      departmentId: defaultValues?.departmentId ?? initialDepartmentId,
    },
  });
  const listName = form.watch('user_data');
  const departmentId = form.watch('departmentId');
  const isSubmitDisabled = !listName || !departmentId;

  const loadOptions = useCallback(async (inputValue: string): Promise<TOption[]> => {
    const { departments = [] } = await getDepartments({ ...(inputValue ? { name: inputValue } : {}) });
    return departments.map((department) => ({
      value: department.department_id,
      label: department.name ?? department.department_id,
    }));
  }, []);

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <div
        className={st.Root}
        data-testid="listFormCreate"
      >
        <h5>{t('title', { context })}</h5>

        {context === 'duplicate' && (
          <span className={st.Subtitle}>
            {t('subtitle', { name: duplicateSourceName })}
          </span>
        )}

        <div className={st.Fields}>
          <div className={st.InputWrapper}>
            <Input
              id="user_data"
              label={t('user_data.label')}
              placeholder={t('user_data.placeholder')}
              {...form.register('user_data', {
                required: validate.required(),
                maxLength: validate.maxLength(128),
              })}
              errors={form.formState.errors}
              required
            />
          </div>

          {!initialDepartmentId && context === 'create' && (
            <div>
              <ControllableValueSelect
                // ControllableValueSelect пока не дженерик, поэтому приводим тип локально.
                cacheOptions
                control={form.control}
                defaultOptions
                errors={form.formState.errors}
                id="departmentId"
                isAsync
                isClearable
                label={t('department.label')}
                loadOptions={loadOptions}
                name="departmentId"
                noOptionsMessage={() => t('department.empty')}
                placeholder={t('department.placeholder')}
                required
                rules={{ required: validate.required() }}
              />
            </div>
          )}

          {!initialDepartmentId && context === 'duplicate' && (
            <div>
              <OwnerDepartmentSelect
                control={form.control}
                errors={form.formState.errors}
                label={t('department.label')}
                name="departmentId"
                required
                setValue={form.setValue}
              />
            </div>
          )}
        </div>

        <div className={st.Actions}>
          <Button
            className={st.Button}
            data-testid="listFormCreate.submitButton"
            disabled={isSubmitDisabled}
            type="submit"
          >
            {t('submit_create')}
          </Button>

          <Button
            className={st.Button}
            data-testid="listFormCreate.cancelButton"
            kind="secondary"
            onClick={onCancel}
            variant="outlined"
          >
            {t('cancel')}
          </Button>
        </div>
      </div>
    </form>
  );
}

export { ListFormPoint3 };
